import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { ReactNode, useState } from 'react';
import { Controller } from 'react-hook-form';
import { RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import { useMyDataStore } from 'src/store/me/me.store';

type Props = {
  methods: any;
  onSubmit: VoidFunction;
  isEdit: boolean;
  isSubmitting: boolean;
  multipleOptions: boolean[];
  multipleLabel: string[];
  values: any;
};

type SectionKey = 'info' | 'redeems';

type SectionCardProps = {
  section: SectionKey;
  index: number;
  title: string;
  expandedSections: Record<SectionKey, boolean>;
  onToggleSection: (section: SectionKey) => void;
  children: ReactNode;
};

function SectionTitle({ index, title }: { index: number; title: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}
      >
        {index}
      </Box>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );
}

function SectionCard({
  section,
  index,
  title,
  expandedSections,
  onToggleSection,
  children,
}: SectionCardProps) {
  const expanded = expandedSections[section];

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <SectionTitle index={index} title={title} />
          <IconButton onClick={() => onToggleSection(section)}>
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
          <Box sx={{ pt: 1 }}>{children}</Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default function ThemeForm({
  methods,
  onSubmit,
  isEdit,
  isSubmitting,
  multipleOptions,
  multipleLabel,
  values,
}: Props) {
  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    info: true,
    redeems: true,
  });

  const toggleSection = (section: SectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={3}>
        <Controller
          name="status"
          control={methods.control}
          render={({ field }) => (
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={1}
              sx={{ p: 1 }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {field.value === 'active' ? 'Ativo' : 'Inativo'}
              </Typography>
              <Switch
                checked={field.value === 'active'}
                onChange={(_, checked) => field.onChange(checked ? 'active' : 'inactive')}
              />
            </Stack>
          )}
        />

        <SectionCard
          section="info"
          index={1}
          title="Informações"
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
        >
          <RHFTextField name="name" label="Nome" variant="filled" />
        </SectionCard>

        <SectionCard
          section="redeems"
          index={2}
          title="Configuração de resgates"
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
        >
          <Stack spacing={2}>
            <Stack>
              <Typography variant="body1" fontWeight={600}>
                Múltiplos resgates por usuário:
              </Typography>
              <RHFRadioGroupLegacy
                name="multipleRedeem"
                options={multipleOptions}
                getOptionLabel={multipleLabel}
              />
            </Stack>

            {!!values.multipleRedeem && values.multipleRedeem !== 'false' && (
              <RHFTextField
                name="redeemQuantity"
                label="Quantidade de resgates"
                type="number"
                variant="filled"
              />
            )}
          </Stack>
        </SectionCard>

        <Stack alignItems="flex-end">
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={(isEdit && !permissions?.update) || (!isEdit && !permissions?.create)}
          >
            {!isEdit ? 'Cadastrar Tema' : 'Editar Tema'}
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
